import { createAsyncThunk } from '@reduxjs/toolkit';
import {
  IDeleteClassroomRequest,
  IClassroomDeleteSuccessResponse,
  IClassroomDeleteGatedResponse,
} from '../../interface/classRoomModel';
import { deleteClassroomFn } from '../../services/classRoom';
import { tr } from '../../i18n/i18n';

/**
 * CVA-2933: Delete classroom thunk (gated cascade delete).
 *
 * - On 200: returns IClassroomDeleteSuccessResponse in fulfilled.payload.
 * - On 400 gate (hard block or soft gate): rejectWithValue receives the
 *   structured IClassroomDeleteGatedResponse so the component can read
 *   `errorCode` (+ `data` impact summary) and show the correct modal.
 * - On any other error: rejectWithValue receives a generic { message } object.
 *
 * NOTE: the shared axios instance (`services/api.tsx`) rejects with
 * `error.response.data` directly (see its response interceptor), so the value
 * caught here is already the parsed response body — NOT an AxiosError.
 */
export const deleteClassroom = createAsyncThunk<
  IClassroomDeleteSuccessResponse,
  { classroomId: string; payload?: IDeleteClassroomRequest },
  { rejectValue: IClassroomDeleteGatedResponse | { message: string } }
>('classroom/delete', async ({ classroomId, payload }, { rejectWithValue }) => {
  try {
    const response = await deleteClassroomFn(classroomId, payload);
    return response.data as IClassroomDeleteSuccessResponse;
  } catch (err) {
    const body = err as Partial<IClassroomDeleteGatedResponse> & { message?: string };

    // Structured gate response (400 body carries a machine-readable errorCode) —
    // pass it through so the component can decide which modal to show.
    if (body?.errorCode) {
      return rejectWithValue(body as IClassroomDeleteGatedResponse);
    }

    return rejectWithValue({
      message: body?.message || tr('classroom.error_delete', 'Failed to delete classroom'),
    });
  }
});

export const classroomActions = {
  deleteClassroom,
};
